var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var BlueprintComponent_1;
import ComponentRegistry from '@slickdeals/durango/dist/javascript/core/componentRegistry';
import Injector, { InjectorScope } from '@slickdeals/durango/dist/javascript/core/injector';
import { applyDefaultOptions, hasDefaultOptions } from '@slickdeals/durango/dist/javascript/decorators/defaultOptions';
import { inject } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { registerable } from '@slickdeals/durango/dist/javascript/decorators/registerable';
import { name as callbackQueueName } from '@slickdeals/durango/dist/javascript/queues/callback/callbackQueue';
import { awaitProperty } from '@slickdeals/durango/dist/javascript/utilities/awaitProperty';
import { isHtmlElement } from '@slickdeals/durango/dist/javascript/utilities/guards';
import readyState from 'ready-state';
import { instantiate as instantiateHydration, getHydrationData } from './dataHydration';
// eslint-disable-next-line import/no-cycle
/** The name of the module */
export const name = 'BlueprintComponent';
export var ClassNames;
(function (ClassNames) {
    ClassNames["Wrapper"] = "blueprint";
})(ClassNames || (ClassNames = {}));
// CallbackQueue key for the init callbacks
const INIT_QUEUE_KEY = 'init';
// Dependencies for method-level injection of the init functions
const queueDependencies = {
    ...Injector.createDependency(callbackQueueName, InjectorScope.Singleton),
};
// Default options for the instantiate() function
const defaultInstantiateOptions = {
    fromHydrationQueue: true,
    fromDom: false,
    fromMutation: false,
};
let BlueprintComponent = BlueprintComponent_1 = class BlueprintComponent {
    /**
     * Create a Blueprint component
     *
     * @param {Object} options Data and options to initialize the component with
     */
    constructor(moduleNameOrJsxOptions, options = {}) {
        this.options = options;
        // Are all the initialization calls completed for this component?
        this._isInitialized = false;
        // If JsxOptions are passed in, don't do anything
        // This will never happen, guarding since options are allowed to be passed in.
        // Normally, with React, any props set on a module will hit React's constructor. Since we
        // aren't using React and our own JsxFactory to handle Jsx code, TypeScript doesn't
        // resolve that props aren't going to hit the constructor of our class, but our custom factory function.
        // Because of that, we need to type the constructor to take in props so it doesn't error.
        if (typeof moduleNameOrJsxOptions === 'object') {
            return;
        }
        // If the object has default options defined, then merge them with the specified options
        // Only do this if the object has keys (options to apply)
        this.options = hasDefaultOptions(this) ? applyDefaultOptions(this, options) : options;
        Object.freeze(this.options);
        // Since init() is expected to do DOM traversal/manipulation, defer it until
        // the page is loaded
        readyState.domready
            .then(this.init.bind(this))
            .then(this.registerElement.bind(this, moduleNameOrJsxOptions))
            .then(this.processInitQueue.bind(this))
            .then(this.initComplete.bind(this));
    }
    /**
       * Placeholder method for extending classes to do any DOM/module initialization
       */
    init() {
        this._isInitialized = false; // eslint-disable-line no-underscore-dangle
    }
    /**
       * Process all callbacks (if any) in the init queue
       */
    async processInitQueue(callbackQueue) {
        await callbackQueue.fire(this.constructor.name, INIT_QUEUE_KEY, this);
    }
    initComplete() {
        this._isInitialized = true; // eslint-disable-line no-underscore-dangle
    }
    /**
     * Perform any required cleanup to properly destroy the component
     *
     * @param cleanDom Should dynamically-inserted DOM elements be removed?
     */
    destroy(cleanDom) { } // eslint-disable-line class-methods-use-this, @typescript-eslint/no-unused-vars
    async isInitialized() {
        try {
            await awaitProperty('_isInitialized', this);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Register a module/component for reverse lookup by an HTML element
     *
     * @param moduleName Name of the component being registered
     */
    registerElement(moduleName) {
        if (this.element) {
            ComponentRegistry.add(this.element, moduleName, this);
        }
    }
    /**
       * Add a callback function that will be executed as part of the component init process
       *
       * @param moduleName Name of the module to add the callback to
       * @param callback The callback function to execute
       */
    static addToInitQueue(moduleName, callback, callbackQueue) {
        callbackQueue.add(moduleName, INIT_QUEUE_KEY, callback);
    }
    /**
       * Called from inside an instantiable module to instantiate a new instance of a module with hydration data
       *
       * @param {key} string - The data key used to store the module's data in the hydration
       */
    static async instantiate(moduleName, options = defaultInstantiateOptions) {
        await readyState.domready;
        // Instantiates any components for the given key from the data hydration queue
        if (options.fromHydrationQueue) {
            // Get the hydration data for the given key
            const data = getHydrationData(moduleName);
            instantiateHydration(moduleName, data);
        }
        // Instantiates any components for the given key ([data-blueprint="${key}"]) from existing DOM elements on the page
        if (options.fromDom) {
            // Fetch the DOM elements with the provided key
            const elements = document.querySelectorAll(`[data-blueprint="${moduleName}"]`);
            // Instantiate the components
            BlueprintComponent_1.instantiateFromDataAttributes(elements);
        }
        // Instantiates any components added to the page after DOMready for the given key ([data-blueprint="${key}"])
        if (options.fromMutation) {
            // Define a mutation observer to watch for elements being added
            const observer = new MutationObserver((mutationsList) => {
                // Elements that will need instantiation
                const elements = [];
                // Loop through the provided mutations and check for matches
                mutationsList.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        // Check if the 'data-blueprint' attribute exists and if it matches
                        if (!isHtmlElement(node)) {
                            return;
                        }
                        const blueprint = node.dataset.blueprint || '';
                        if (blueprint.toLowerCase() === moduleName.toLowerCase()) {
                            // If it matches, add it to the collection of elements to instantiate
                            elements.push(node);
                            return;
                        }
                        const children = node.querySelectorAll(`[data-blueprint="${moduleName}"]`);
                        children.forEach(childNode => {
                            elements.push(childNode);
                        });
                    });
                });
                // If any elements exist for instantiation, then instantiate them
                if (elements.length) {
                    BlueprintComponent_1.instantiateFromDataAttributes(elements);
                }
            });
            // Watch for subtree additions (including in child nodes) only
            const config = {
                subtree: true,
                childList: true,
            };
            // Register the observer on the entire document
            observer.observe(document, config);
        }
    }
    /**
     * Asynchronous function for waiting until 'this.element' is available
     */
    async isElementAvailable() {
        try {
            await awaitProperty('element', this);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Get the HTML element used by the component
     */
    getElement() {
        return this.element;
    }
    /**
     * Using the 'data-blueprint="{ComponentName"}' data attribute, initializes components based
     * on the component name provided.
     *
     * @param elements Collection of HTML elements to create component instances for
     */
    static instantiateFromDataAttributes(elements) {
        elements.forEach((element) => {
            // If the data attribute is present
            if (element.dataset.blueprint) {
                // Fetch the proper constructor from the injector
                const Component = Injector.getConstructor(element.dataset.blueprint);
                // If the constructor was valid, create the component
                if (typeof Component !== 'undefined') {
                    Component.createFromDataAttributes(element);
                }
            }
        });
    }
    /**
       * Create a formatted ID for an element. If an ID is supplied and it is already "namespaced" with the module name,
       * then it simply returns that ID untouched.
       *
       * @param {String} id - The ID for the element
       * @param {String} moduleName - The name of the module/UI Component
       *
       * @return {String} The formatted ID
       *
       * @example
       * generateComponentId('testId', 'TestModule'); // Outputs 'testId-TestModule'
       */
    static generateComponentId(id, moduleName) {
        return id.endsWith(`-${moduleName}`) ? id : `${id}-${moduleName}`;
    }
    // Disabling the any here because the options can be of any value
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static async loadElement(moduleName, options) {
        await readyState.domready;
        if (typeof options.element === 'object' && options.element instanceof HTMLElement) {
            return options.element;
        }
        if (typeof options.id !== 'string') {
            return undefined;
        }
        let element;
        element = document.getElementById(options.id);
        if (!element) {
            element = document.getElementById(BlueprintComponent_1.generateComponentId(options.id, moduleName));
        }
        return element;
    }
};
__decorate([
    inject(queueDependencies)
], BlueprintComponent.prototype, "processInitQueue", null);
__decorate([
    inject(queueDependencies)
], BlueprintComponent, "addToInitQueue", null);
BlueprintComponent = BlueprintComponent_1 = __decorate([
    registerable
], BlueprintComponent);
export default BlueprintComponent;
